import { createContext } from 'react';
import type {
  ShellPosition,
  ShellSize,
} from '../components/ora/Global/CompanionShell/useDragPosition';

/**
 * Which renderer is on screen (ORA-645). The push drawer owns the right-edge
 * gutter; the floating shell owns nothing of the page, so the body classes
 * that reserve that gutter must follow the active layout rather than mere
 * availability.
 */
export type AIDrawerLayout = 'drawer' | 'shell';

/**
 * How much of the screen the floating shell takes. `isOpen` stays the single
 * source of truth for "the conversation is on screen", so `bubble` is exactly
 * the closed state.
 */
export type CompanionShellMode = 'bubble' | 'window' | 'expanded';

/**
 * Which support channel is on screen (ORA-646). The two never share the
 * screen: opening Intercom minimizes our shell, and Intercom's Messenger sits
 * in the same corner at the same layer.
 *
 * There is deliberately no "← AI" control in our header, because our header is
 * not on screen while Intercom is: the way back is Intercom's own close, which
 * returns the channel to `ai` and the bubble to the corner, from where a click
 * (or `c`) reopens the conversation exactly as it was left.
 */
export type CompanionSupportChannel = 'ai' | 'human';

// Context types
interface AIDrawerContextValue {
  open: (options?: AIDrawerOptions) => void;
  close: () => void;
  isOpen: boolean;
  isAvailable: boolean;
  /**
   * Whether the drawer renderer is currently mounted (it lives inside
   * `ProtectedLayout`, so large parts of the app — `/faculty/*`, auth screens —
   * never render it). Entry points elsewhere in the chrome must not pretend the
   * drawer can open when nothing is there to open.
   */
  setIsMounted: (isMounted: boolean) => void;
  /**
   * Whether the surface the student is on deliberately hides the drawer (a live
   * self-assessment attempt, the full-page `/chat` route, the games section).
   */
  setIsSuppressed: (isSuppressed: boolean) => void;
  /**
   * Whether the live webinar page is on a study phase (a QBank question or
   * flashcards). The renderer hides the drawer on the webinar route, and the
   * page lifts that only while the student is actually studying — the host-led
   * phases (welcome, polls, video, games, thanks) stay drawer-free.
   */
  isWebinarStudyPhase: boolean;
  setIsWebinarStudyPhase: (isWebinarStudyPhase: boolean) => void;
  /**
   * Set by a page that has taken over the whole screen (a game round forcing
   * the fullscreen window variant). The companion stands down for as long as
   * it is true: a floating bubble over a timed round is in the way, and the
   * round is not a surface to ask questions about.
   */
  setIsPageSuppressed: (isPageSuppressed: boolean) => void;
  isPageSuppressed: boolean;
  /**
   * Whether a launcher bubble belongs on this surface at this width (the
   * narrow-viewport sheet's only opener). Reported by the renderer; the OS
   * shell reads it to put the bubble in its bottom nav row, beside the tabs.
   */
  hasMobileLauncher: boolean;
  setHasMobileLauncher: (hasMobileLauncher: boolean) => void;
  /**
   * Whether the OS shell is rendering its bottom nav row, which is where the
   * launcher goes when it is. Without one (plain routes) the drawer floats
   * its own copy instead.
   */
  hasMobileNavRow: boolean;
  setHasMobileNavRow: (hasMobileNavRow: boolean) => void;
  /** Reported by the renderer; see `AIDrawerLayout`. */
  setLayout: (layout: AIDrawerLayout) => void;
  layout: AIDrawerLayout;
  /**
   * Whether the `c` hotkey may open the chat here. The renderer turns it off
   * on the games routes (ORA-664): Doctordle reads bare letters off the
   * window, and a `C` in a guess must not also toggle the companion.
   */
  isHotkeyEnabled: boolean;
  setIsHotkeyEnabled: (isEnabled: boolean) => void;
  shellMode: CompanionShellMode;
  /** `bubble` closes the shell; anything else opens it in that mode. */
  setShellMode: (mode: CompanionShellMode) => void;
  /** Top-left of the shell window, or null while it sits at its default. */
  shellPosition: ShellPosition | null;
  setShellPosition: (position: ShellPosition) => void;
  /**
   * Aspect-locked window size the panel renders at (ORA-649): the size the
   * student resized to, or the largest the viewport holds (ORA-696). Null only
   * where a consumer has no provider behind it.
   */
  shellSize: ShellSize | null;
  setShellSize: (size: ShellSize) => void;
  /** Sends the window back to its default corner and its default size. */
  resetShellPosition: () => void;
  supportChannel: CompanionSupportChannel;
  /**
   * Hand the student to Intercom. The channel does not change here: only
   * Intercom's own `onShow` says the Messenger is really up, so a widget that
   * never arrives (blocked script, native shell, headless) cannot leave the
   * student on a page with the shell minimized and nothing in its place.
   */
  openHumanSupport: () => void;
  /**
   * Set when the Messenger could not be opened, for the shell to show in place
   * of the conversation the student expected. Null the rest of the time.
   */
  humanSupportError: string | null;
  /** Unread Intercom replies, shown on the bubble's badge. */
  intercomUnreadCount: number;
  /** Set by an `open` that named a conversation; cleared once acted on. */
  threadTarget: AIDrawerThreadTarget | null;
  clearThreadTarget: () => void;
  isPushModeAvailable: boolean;
  isPushModeActive: boolean;
}

export interface AIDrawerOptions {
  /**
   * Open on a specific conversation rather than whatever the drawer last had
   * (ORA-681). Coming back from a study set is the only caller: the thread the
   * set was served in is not necessarily the drawer's current one.
   */
  conversationId?: string;
  /** A block in that conversation to scroll to once the thread has rendered. */
  scrollToBlockId?: string;
}

/**
 * One request to reopen the companion on a particular block. `nonce` makes a
 * second return to the same block a new request rather than a no-op.
 */
export interface AIDrawerThreadTarget {
  conversationId: string;
  scrollToBlockId: string | null;
  nonce: number;
}

// Create context
export const AIDrawerContext = createContext<AIDrawerContextValue | undefined>(
  undefined
);
